<script>
  import {
    getCardsToRemove,
    getCardsToReceive,
    getUserBinderUrl,
    getCardKey,
    generateCombinedCsv,
    generateMoxfieldCsv,
  } from '../../lib/logic/moxfield-sync.js';
  import { addToast } from '../../lib/stores/notifications.svelte.js';

  let { isOpen = $bindable(false), trade, userSide } = $props();

  let csvType = $state('all');
  let excludedKeys = $state(new Set());

  const cardsToRemove = $derived(trade ? getCardsToRemove(trade, userSide) : []);
  const cardsToReceive = $derived(trade ? getCardsToReceive(trade, userSide) : []);
  const userBinderUrl = $derived(trade ? getUserBinderUrl(trade, userSide) : null);

  // Reset exclusions when modal opens
  $effect(() => {
    if (isOpen) {
      excludedKeys = new Set();
      csvType = 'all';
    }
  });

  function isExcluded(card) {
    return excludedKeys.has(getCardKey(card));
  }

  function toggleExclusion(card) {
    const key = getCardKey(card);
    const next = new Set(excludedKeys);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    excludedKeys = next;
  }

  function downloadCsv() {
    let csv;
    let filename;

    switch (csvType) {
      case 'removals':
        if (cardsToRemove.length === 0) {
          addToast('No cards to remove', { type: 'removed' });
          return;
        }
        csv = generateCombinedCsv(cardsToRemove, [], new Set());
        filename = 'moxfield-removals.csv';
        break;
      case 'additions': {
        const hasAdditions = cardsToReceive.some((c) => !excludedKeys.has(getCardKey(c)));
        if (!hasAdditions) {
          addToast('No cards to add', { type: 'removed' });
          return;
        }
        csv = generateCombinedCsv([], cardsToReceive, excludedKeys);
        filename = 'moxfield-additions.csv';
        break;
      }
      default: {
        const hasRemovals = cardsToRemove.length > 0;
        const hasAny = cardsToReceive.some((c) => !excludedKeys.has(getCardKey(c)));
        if (!hasRemovals && !hasAny) {
          addToast('No changes to sync', { type: 'removed' });
          return;
        }
        csv = generateCombinedCsv(cardsToRemove, cardsToReceive, excludedKeys);
        filename = 'moxfield-sync.csv';
      }
    }

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    addToast('CSV downloaded!', { type: 'trade' });
  }

  function downloadReaddCsv() {
    if (cardsToRemove.length === 0) {
      addToast('No cards to re-add', { type: 'removed' });
      return;
    }
    const csv = generateMoxfieldCsv(cardsToRemove);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'moxfield-readd.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    addToast('Re-add CSV downloaded!', { type: 'trade' });
  }

  function openMoxfieldBinder() {
    if (userBinderUrl) {
      window.open(userBinderUrl, '_blank');
    }
  }
</script>

{#if isOpen}
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div class="modal-backdrop" onclick={() => (isOpen = false)}>
    <!-- svelte-ignore a11y_click_events_have_key_events -->
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div class="modal" onclick={(e) => e.stopPropagation()}>
      <div class="modal-header">
        <h2>Sync to Moxfield</h2>
        <button class="modal-close" onclick={() => (isOpen = false)} aria-label="Close">
          <svg
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            ><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"
            ></line></svg
          >
        </button>
      </div>

      <div class="modal-body">
        <!-- Removals section -->
        <div class="sync-section" class:empty={cardsToRemove.length === 0}>
          <h3 class="sync-section-title sync-removals-title">
            Cards to Remove
            <span class="sync-count">{cardsToRemove.length}</span>
          </h3>
          {#if cardsToRemove.length === 0}
            <p class="sync-empty">No cards to remove</p>
          {:else}
            <div class="sync-card-list">
              {#each cardsToRemove as card (getCardKey(card))}
                <div class="sync-card-item sync-card-removal">
                  <span class="sync-card-qty sync-qty-negative">-{card.quantity || 1}</span>
                  <span class="sync-card-name">{card.name || 'Unknown'}</span>
                  <span class="sync-card-set">
                    {(card.set || '').toUpperCase()}{card.isFoil ? ' (Foil)' : ''}
                  </span>
                </div>
              {/each}
            </div>
          {/if}
        </div>

        <!-- Additions section -->
        <div class="sync-section" class:empty={cardsToReceive.length === 0}>
          <h3 class="sync-section-title sync-additions-title">
            Cards to Add
            <span class="sync-count">{cardsToReceive.length}</span>
          </h3>
          {#if cardsToReceive.length === 0}
            <p class="sync-empty">No cards to add</p>
          {:else}
            <div class="sync-card-list">
              {#each cardsToReceive as card (getCardKey(card))}
                <!-- svelte-ignore a11y_click_events_have_key_events -->
                <!-- svelte-ignore a11y_no_static_element_interactions -->
                <div
                  class="sync-card-item sync-card-addition"
                  class:excluded={isExcluded(card)}
                  onclick={() => toggleExclusion(card)}
                >
                  <input
                    type="checkbox"
                    class="sync-card-checkbox"
                    checked={!isExcluded(card)}
                    onclick={(e) => e.stopPropagation()}
                    onchange={() => toggleExclusion(card)}
                  />
                  <span class="sync-card-qty sync-qty-positive">+{card.quantity || 1}</span>
                  <span class="sync-card-name">{card.name || 'Unknown'}</span>
                  <span class="sync-card-set">
                    {(card.set || '').toUpperCase()}{card.isFoil ? ' (Foil)' : ''}
                  </span>
                </div>
              {/each}
            </div>
          {/if}
        </div>
      </div>

      <div class="modal-footer">
        <div class="footer-row">
          <select class="csv-type-select" bind:value={csvType}>
            <option value="all">All Changes</option>
            <option value="removals">Removals Only</option>
            <option value="additions">Additions Only</option>
          </select>
          <button class="btn btn-primary" onclick={downloadCsv}>
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              ><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline
                points="7 10 12 15 17 10"
              ></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg
            >
            Download CSV
          </button>
        </div>
        <div class="footer-row footer-secondary">
          {#if userBinderUrl}
            <button class="btn btn-outline" onclick={openMoxfieldBinder}>
              Open Moxfield Binder
            </button>
          {/if}
          {#if cardsToRemove.length > 0}
            <button class="btn btn-outline btn-small" onclick={downloadReaddCsv}>
              Download Re-add CSV
            </button>
          {/if}
        </div>
      </div>
    </div>
  </div>
{/if}

<style>
  .modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    z-index: 10000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
  }

  .modal {
    background: white;
    border-radius: 12px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    max-width: 480px;
    width: 100%;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.25rem;
    border-bottom: 1px solid #e5e7eb;
    flex-shrink: 0;
  }

  .modal-header h2 {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 600;
    color: #1f2937;
  }

  .modal-close {
    width: 32px;
    height: 32px;
    border: none;
    background: transparent;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #6b7280;
    transition: background 0.15s;
  }

  .modal-close:hover {
    background: #f3f4f6;
  }

  .modal-body {
    flex: 1;
    overflow-y: auto;
    padding: 1rem 1.25rem;
  }

  .sync-section {
    margin-bottom: 1rem;
  }

  .sync-section:last-child {
    margin-bottom: 0;
  }

  .sync-section.empty {
    opacity: 0.5;
  }

  .sync-section-title {
    font-size: 0.8rem;
    font-weight: 600;
    margin: 0 0 0.5rem 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .sync-removals-title {
    color: #ef4444;
  }

  .sync-additions-title {
    color: #22c55e;
  }

  .sync-count {
    font-size: 0.65rem;
    padding: 0.1rem 0.4rem;
    border-radius: 9999px;
    background: #f3f4f6;
    color: #6b7280;
  }

  .sync-empty {
    font-size: 0.8rem;
    color: #9ca3af;
    font-style: italic;
    margin: 0;
  }

  .sync-card-list {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .sync-card-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.35rem 0.5rem;
    border-radius: 6px;
    font-size: 0.8rem;
    transition:
      background 0.15s,
      opacity 0.15s;
  }

  .sync-card-removal {
    background: #fef2f2;
  }

  .sync-card-addition {
    background: #f0fdf4;
    cursor: pointer;
  }

  .sync-card-addition:hover {
    background: #dcfce7;
  }

  .sync-card-addition.excluded {
    opacity: 0.4;
    text-decoration: line-through;
  }

  .sync-card-checkbox {
    accent-color: #22c55e;
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    cursor: pointer;
  }

  .sync-card-qty {
    font-weight: 700;
    min-width: 24px;
    flex-shrink: 0;
  }

  .sync-qty-negative {
    color: #ef4444;
  }

  .sync-qty-positive {
    color: #22c55e;
  }

  .sync-card-name {
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: #1f2937;
    font-weight: 500;
  }

  .sync-card-set {
    font-size: 0.7rem;
    color: #6b7280;
    flex-shrink: 0;
  }

  .modal-footer {
    padding: 0.75rem 1.25rem;
    border-top: 1px solid #e5e7eb;
    background: #f9fafb;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .footer-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .footer-secondary {
    justify-content: center;
  }

  .csv-type-select {
    padding: 0.4rem 0.6rem;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 0.8rem;
    background: white;
    cursor: pointer;
    flex: 1;
  }

  .btn {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-size: 0.8rem;
    font-weight: 600;
    cursor: pointer;
    transition:
      background 0.15s,
      transform 0.1s;
    border: none;
    font-family: inherit;
  }

  .btn:active {
    transform: scale(0.97);
  }

  .btn-primary {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
  }

  .btn-primary:hover {
    opacity: 0.9;
  }

  .btn-outline {
    background: transparent;
    border: 1px solid #d1d5db;
    color: #374151;
    padding: 0.35rem 0.75rem;
    font-size: 0.75rem;
  }

  .btn-outline:hover {
    background: #f3f4f6;
  }

  @media (max-width: 768px) {
    .modal {
      max-height: 90vh;
    }
  }
</style>
